import { CheckoutRequiredState } from 'types/checkout';
import { investmentEntity } from '@store/modules/investment-entity';
import { isEligibleForAdditionalInvestment } from '@api/checkout';
import { orderForm } from '@store/modules/order-form';
import { questionnaire } from '@store/modules/questionnaire';
import { user } from '@store/modules/user';

export const isIRA = async (): Promise<boolean> => {
	if (user.hasFirstLastName) {
		if (investmentEntity.isIraEntity) {
			if (orderForm.signUpCheckoutType === 'TAXABLE') {
				await orderForm.submitSignupCheckoutType('IRA');
			}
		} else if (user.hasIraEntity && orderForm.signUpCheckoutType === 'IRA') {
			await orderForm.submitSignupCheckoutType('TAXABLE');
		}
	}
	return orderForm.signUpCheckoutType === 'IRA';
};

export const notIRA = async (): Promise<boolean> => {
	return !(await isIRA());
};

const orderGroupCreated = (): boolean => {
	return !!orderForm.orderGroupId;
};

const paymentMethodAdded = (): boolean => {
	return !!orderForm.paymentMethod;
};

const acknowledgmentsAccepted = (): boolean => {
	return !!orderForm.acknowledgmentsDocIds;
};

const checkSignupAndCountrySteps = (): string | undefined => {
	if (!user.hasFirstLastName) {
		return 'checkout-signup';
	} else if (!orderForm.countriesSubmitted) {
		return 'checkout-select-country';
	} else if (!orderForm.residenceValid) {
		return 'checkout-country-not-supported';
	} else if (orderForm.signUpCheckoutType === 'IRA') {
		if (user.hasIraEntity) {
			return 'ira-start';
		}
		return 'ira-landing';
	}

	return undefined;
};

const getOrderGroupStep = (): string => {
	if (!paymentMethodAdded()) {
		return 'checkout-payment-method';
	} else if (!acknowledgmentsAccepted()) {
		return 'checkout-acknowledgments';
	} else {
		return 'checkout-review-order';
	}
};

export const goToStartCheckoutStep = async (
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	next: any
): Promise<void> => {
	const stepName =
		checkSignupAndCountrySteps() || orderForm.isUnclaimedGiftedAccount
			? 'checkout-individual-account-details'
			: 'checkout-account-type';
	await next({ name: stepName });
};

export const goToResumeCheckoutStep = async (
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	next: any
): Promise<void> => {
	if (investmentEntity.entityType !== 'INDIVIDUAL') {
		await next({ name: 'checkout-account-type' });
	} else {
		const stepName = orderForm.isUnclaimedGiftedAccount ? 'checkout-acknowledgments' : getOrderGroupStep();
		await next({ name: stepName });
	}
};

export const redirectToQuestionnaire = async (
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	next: any
): Promise<void> => {
	if (!questionnaire.dataRetrieved) {
		await questionnaire.getFullResponse(true);
	}
	if (questionnaire.fullResponse.completionDate) {
		await next({ name: 'questionnaire-results', replace: true });
	} else {
		await next({ name: 'questionnaire', replace: true });
	}
};

export const isIneligibleForCheckout = async (): Promise<boolean> => {
	return (
		investmentEntity.entityType === 'IRA' ||
		investmentEntity.isClaimedGiftedAccount ||
		(!!investmentEntity.investmentEntityId &&
			(await isEligibleForAdditionalInvestment(investmentEntity.investmentEntityId)))
	);
};

export const getValidCheckoutStep = (): string => {
	const signupOrCountryNeeded = checkSignupAndCountrySteps();
	if (signupOrCountryNeeded) {
		return signupOrCountryNeeded;
	} else if (!orderForm.hasSubmittedAccountDetails) {
		return orderForm.isUnclaimedGiftedAccount ? 'checkout-individual-account-details' : 'checkout-account-type';
	} else if (orderForm.isUnclaimedGiftedAccount) {
		return 'checkout-acknowledgments';
	} else if (!orderGroupCreated()) {
		return 'checkout-payment-amount';
	} else {
		return getOrderGroupStep();
	}
};

const checkState = async (requiredState: CheckoutRequiredState): Promise<boolean> => {
	switch (requiredState) {
		case 'userNotCreated':
			return !user.hasFirstLastName;
		case 'userCreated':
			return user.hasFirstLastName;
		case 'residenceInvalidOrNotSubmitted':
			return !orderForm.countriesSubmitted || !orderForm.residenceValid;
		case 'residenceValid':
			return orderForm.residenceValid;
		case 'accountDetailsSubmitted':
			return orderForm.hasSubmittedAccountDetails;
		case 'notIRA':
			return await notIRA();
		case 'orderGroupCreated':
			return orderGroupCreated();
		case 'paymentMethodAdded':
			return paymentMethodAdded();
		case 'acknowledgmentsAccepted':
			return acknowledgmentsAccepted();
		case 'notGifted':
			return !orderForm.isUnclaimedGiftedAccount;
		default:
			return user.hasFirstLastName;
	}
};

const checkRequiredStates = async (requiredStates: Array<CheckoutRequiredState>): Promise<boolean> => {
	for (let i = 0; i < requiredStates.length; i++) {
		if (!(await checkState(requiredStates[i]))) {
			return false;
		}
	}
	return true;
};

export const verifyRequiredStates = async (
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	next: any,
	requiredStates: Array<CheckoutRequiredState>
): Promise<void> => {
	const isValidStep = await checkRequiredStates(requiredStates);

	if (isValidStep) {
		await next();
	} else {
		const stepName = getValidCheckoutStep();
		await next({ name: stepName, replace: true });
	}
};
